/**
 * StretchStatusBar — Top bar showing stretch number, elapsed time,
 * player readiness, light sources, and current phase.
 */

import { Clock, Flame, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StretchState } from '@omen-quest/stretch-engine';

interface StretchStatusBarProps {
  state: StretchState;
  className?: string;
}

export function StretchStatusBar({ state, className }: StretchStatusBarProps) {
  const elapsedMinutes = state.stretchNumber * 10;
  const committedCount = state.players.filter(p => p.hasCommitted).length;

  // Best active light source
  const activeLights = state.crawlStrategy.lightSources.filter(s => s.remainingStretches > 0);
  const bestLight = activeLights.length > 0
    ? activeLights.reduce((a, b) => a.remainingStretches < b.remainingStretches ? a : b)
    : null;

  const phaseLabels: Record<string, string> = {
    planning: 'Planning',
    resolving: 'Resolving...',
    events: 'Events',
    ended: 'Ended',
  };

  return (
    <div className={cn(
      'flex items-center justify-between px-4 py-2 bg-neutral-900/80 border-b border-white/10 text-sm',
      className,
    )}>
      {/* Left: Stretch number + time */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-white font-medium">
          <Clock className="w-4 h-4 text-neutral-400" />
          <span>Stretch {state.stretchNumber}</span>
          <span className="text-neutral-500">~{elapsedMinutes} min</span>
        </div>

        <div className={cn(
          'px-2 py-0.5 rounded text-xs font-medium',
          state.phase === 'planning' ? 'bg-blue-900/40 text-blue-400' :
          state.phase === 'resolving' ? 'bg-amber-900/40 text-amber-400' :
          state.phase === 'events' ? 'bg-purple-900/40 text-purple-400' :
          'bg-neutral-800 text-neutral-400'
        )}>
          {phaseLabels[state.phase] ?? state.phase}
        </div>
      </div>

      {/* Center: Player readiness */}
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-neutral-400" />
        <div className="flex items-center gap-1">
          {state.players.map(player => (
            <div
              key={player.id}
              title={`${player.name}: ${player.hasCommitted ? 'Ready' : 'Planning'}`}
              className={cn(
                'w-2.5 h-2.5 rounded-full border',
                player.hasCommitted
                  ? 'bg-green-500 border-green-400'
                  : 'bg-transparent border-neutral-500',
              )}
            />
          ))}
        </div>
        <span className="text-neutral-500 text-xs">
          {committedCount}/{state.players.length}
        </span>
      </div>

      {/* Right: Light source + stealth */}
      <div className="flex items-center gap-3">
        {bestLight && (
          <div className={cn(
            'flex items-center gap-1',
            bestLight.remainingStretches <= 2 ? 'text-red-400' :
            bestLight.remainingStretches <= 4 ? 'text-amber-400' :
            'text-yellow-300',
          )}>
            <Flame className="w-4 h-4" />
            <span className="text-xs">{bestLight.remainingStretches}</span>
          </div>
        )}
        {!bestLight && activeLights.length === 0 && (
          <span className="text-xs text-red-400/70">No light</span>
        )}
        {state.crawlStrategy.stealthMode && (
          <span className="text-xs text-purple-400 bg-purple-900/30 px-1.5 py-0.5 rounded">
            Stealth
          </span>
        )}
      </div>
    </div>
  );
}
